import { Alert, AlertTitle, Icons, iconSize, Typography } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type { AiProvenance } from '@munifocus/contracts-gov';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * The label every piece of model output carries. Not a footnote: it sits
 * above the text, says plainly that a model wrote it and that the officer
 * must review it before using it, and lists what the model was not shown.
 */
export function AiLabel({ provenance }: { provenance: AiProvenance }) {
  const { t, i18n } = useTranslation();
  const when = frenchFirstOfMonth(
    new Intl.DateTimeFormat(i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(provenance.generatedAt)),
  );
  const withheld = provenance.redactedFields
    .map((f) => t(`gov.assistant.redacted.${f}`, { defaultValue: '' }))
    .filter(Boolean);
  return (
    <Alert
      severity="warning"
      icon={<Icons.Sparkles size={iconSize.md} aria-hidden />}
      sx={{ mb: 1.5 }}
      data-testid="ai-label"
    >
      <AlertTitle>{t('gov.assistant.aiLabel.title')}</AlertTitle>
      <Typography variant="body2">{t('gov.assistant.aiLabel.body')}</Typography>
      <Typography variant="caption" component="p" sx={{ mt: 0.5 }}>
        {t('gov.assistant.aiLabel.meta', { model: provenance.model, when })}
      </Typography>
      {withheld.length > 0 && (
        <Typography variant="caption" component="p">
          {t('gov.assistant.aiLabel.withheld', { fields: withheld.join(', ') })}
        </Typography>
      )}
    </Alert>
  );
}
